import { useEffect, useMemo, useState } from 'react';
import {
  useGameStore, EventBus, useLocalBubbleId, usePlayerBubbleIds, useProximityUserIds,
} from '@campus/core';
import type { UserStatus } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { Button, Input, Tabs } from '../ds/index.js';
import { PersonAvatar } from './PersonAvatar.js';
import { SidePanel, panelHeaderStyle } from './SidePanel.js';
import { useMembersStore, loadMembers, sessionInfo, apiHeaders } from './membersStore.js';
import { useCampusOnline, refreshCampusPresence, type OnlinePerson } from './presence.js';
import { useConnectionsStore, loadConnections, needsConnection, connectionAction } from './connections.js';
import { canEnterZoneHere } from '../../lib/access.js';

/**
 * Panneau Personnes (tasks/profils-messages-stats-plan.md) : tout le campus
 * d'abord (en ligne, avec la carte où se trouve chacun, puis hors ligne), la
 * bulle ou les personnes à proximité en second onglet. Un clic ouvre la fiche
 * de la personne : message privé, rejoindre sa carte, modération (admins).
 */

export const STATUS_COLORS: Record<UserStatus, string> = {
  available: 'var(--color-status-online)',
  away:      'var(--color-status-away)',
  busy:      'var(--color-status-busy)',
  focus:     '#7F77DD',
};
export const STATUS_LABELS: Record<UserStatus, string> = {
  available: 'Disponible', away: 'Absent', busy: 'Occupé', focus: 'Focus',
};
const ROLE_LABELS: Record<string, string> = { OWNER: 'Propriétaire', ADMIN: 'Admin', MEMBER: 'Membre' };

export function lastSeenLabel(iso: string | null): string {
  if (!iso) return 'Jamais venu sur le campus';
  const minutes = (Date.now() - new Date(iso).getTime()) / 60_000;
  if (minutes < 2) return 'Vu à l’instant';
  if (minutes < 60) return `Vu il y a ${Math.round(minutes)} min`;
  if (minutes < 24 * 60) return `Vu il y a ${Math.round(minutes / 60)} h`;
  const days = Math.round(minutes / (24 * 60));
  if (days < 7) return `Vu il y a ${days} jour${days > 1 ? 's' : ''}`;
  return `Vu le ${new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}`;
}

function moderationRequest(action: 'mute' | 'unmute' | 'kick', userId: string) {
  const slug = useGameStore.getState().currentZoneId?.split(':')[1] ?? '';
  return fetch(`${API_BASE}/world/zones/${slug}/${action}`, {
    method: 'POST', headers: apiHeaders(true), body: JSON.stringify({ userId }),
  }).catch((err) => { console.error(`[PeoplePanel] ${action} error:`, err); });
}

function StatusDot({ status, size = 8 }: { status: UserStatus | 'offline'; size?: number }) {
  return (
    <span aria-hidden="true" style={{
      display: 'inline-block', width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: status === 'offline' ? 'var(--color-status-offline)' : STATUS_COLORS[status],
    }} />
  );
}

interface Person {
  userId: string;
  name: string;
  online: OnlinePerson | null;
  lastSeenAt: string | null;
  role: string | null;
  /** Profil d'accès (ex. Copycampers), null = tout le campus. */
  profileName: string | null;
}

/**
 * Message privé avec quelqu'un d'un autre périmètre (Copycamper ↔ membre de
 * Copyhouse) : demande de connexion d'abord, acceptée par l'autre.
 */
function ConnectionActions({ person }: { person: Person }) {
  const connection = useConnectionsStore((s) => s.byUser.get(person.userId));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (action: 'request' | 'accept' | 'decline' | 'remove') => {
    setBusy(true);
    setError(await connectionAction(person.userId, action));
    setBusy(false);
  };
  const openDm = () => EventBus.emit('ui:open-dm', { userId: person.userId });

  let body: React.ReactNode;
  if (connection?.status === 'accepted') {
    body = (
      <>
        <Button variant="primary" icon="message" onClick={openDm}>Envoyer un message</Button>
        <WhizButton person={person} />
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => void run('remove')}>Retirer la connexion</Button>
      </>
    );
  } else if (connection?.status === 'pending' && connection.direction === 'received') {
    body = (
      <>
        <p className="pl-help" style={{ margin: 0, textAlign: 'center' }}>{person.name} veut se connecter avec toi pour discuter.</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="primary" disabled={busy} onClick={() => void run('accept')} style={{ flex: 1 }}>Accepter</Button>
          <Button variant="secondary" disabled={busy} onClick={() => void run('decline')} style={{ flex: 1 }}>Refuser</Button>
        </div>
      </>
    );
  } else if (connection?.status === 'pending') {
    body = (
      <>
        <Button variant="secondary" disabled>Demande envoyée</Button>
        <Button variant="ghost" size="sm" disabled={busy} onClick={() => void run('remove')}>Annuler la demande</Button>
      </>
    );
  } else if (connection?.status === 'declined' && connection.direction === 'sent') {
    body = <Button variant="secondary" disabled>Demande refusée</Button>;
  } else {
    body = (
      <>
        <Button variant="primary" icon="user-plus" disabled={busy} onClick={() => void run('request')}>Demander à se connecter</Button>
        <p className="pl-help" style={{ margin: 0, textAlign: 'center' }}>Une fois ta demande acceptée, vous pourrez vous écrire.</p>
      </>
    );
  }
  return (
    <>
      {body}
      {error && <p className="pl-help" role="alert" style={{ margin: 0, textAlign: 'center', color: 'var(--color-danger)' }}>{error}</p>}
    </>
  );
}

/**
 * Whiz : l'écran de l'autre tremble avec un petit son. Personne en ligne
 * seulement, jamais en Focus ; mêmes règles qu'un message privé (API).
 */
function WhizButton({ person }: { person: Person }) {
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (state !== 'sent') return;
    const t = setTimeout(() => setState('idle'), WHIZ_COOLDOWN_MS);
    return () => clearTimeout(t);
  }, [state]);
  if (!person.online) return null;
  if (person.online.status === 'focus') {
    return <p className="pl-help" style={{ margin: 0, textAlign: 'center' }}>En Focus : pas de whiz pour l&apos;instant.</p>;
  }
  const send = async () => {
    setState('sending');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/chat/dm/${encodeURIComponent(person.userId)}/whiz`, { method: 'POST', headers: apiHeaders() });
      if (res.ok) { setState('sent'); return; }
      setError(res.status === 429 ? 'Tu viens déjà de lui en envoyer un, attends un peu.' : 'Impossible d\u2019envoyer le whiz.');
    } catch {
      setError('Impossible d\u2019envoyer le whiz.');
    }
    setState('idle');
  };
  return (
    <>
      <Button variant="secondary" icon="bell" disabled={state !== 'idle'} onClick={() => void send()}>
        {state === 'sent' ? 'Whiz envoyé' : 'Envoyer un whiz'}
      </Button>
      {error && <p className="pl-help" role="alert" style={{ margin: 0, textAlign: 'center', color: 'var(--color-danger)' }}>{error}</p>}
    </>
  );
}

/** Même délai que l'API (DirectMessageService.WHIZ_COOLDOWN_MS). */
const WHIZ_COOLDOWN_MS = 30_000;

function PersonRow({ person, you, onOpen }: { person: Person; you: boolean; onOpen: () => void }) {
  const meta = person.online ? person.online.zoneName : lastSeenLabel(person.lastSeenAt);
  return (
    <li>
      <button type="button" className="pl-menu-item" onClick={onOpen}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 48, padding: '4px 8px', borderRadius: 'var(--radius-md)', textAlign: 'left' }}>
        <span style={{ position: 'relative', display: 'inline-flex', opacity: person.online ? 1 : 0.55 }}>
          <PersonAvatar userId={person.userId} name={person.name} size={36} highlight={you} />
          <span style={{ position: 'absolute', right: -1, bottom: -1, display: 'inline-flex', borderRadius: '50%', boxShadow: '0 0 0 2px var(--color-bg-surface)' }}>
            <StatusDot status={person.online?.status ?? 'offline'} size={10} />
          </span>
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: person.online ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>
            {person.name}{you && <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}> (toi)</span>}
          </span>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{meta}</span>
        </span>
      </button>
    </li>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: '12px 8px 4px', font: '700 11px/1 var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-secondary)' }}>
      {children}
    </p>
  );
}

/** Fiche d'une personne. */
function ProfileView({ person, onBack, onClose }: { person: Person; onBack: () => void; onClose: () => void }) {
  const { userId: localUserId, role: localRole, isGuest } = sessionInfo();
  const currentSlug = useGameStore((s) => s.currentZoneId?.split(':')[1] ?? '');
  const isSelf = person.userId === localUserId;
  const isAdmin = localRole === 'OWNER' || localRole === 'ADMIN';
  const canMessage = !isSelf && !isGuest && person.role !== null && !person.userId.startsWith('guest:');
  const gated = canMessage && needsConnection(person.userId);
  const here = person.online && person.online.zoneSlug === currentSlug;
  // Pas de « Rejoindre » vers un lieu hors de son profil d'accès.
  const joinable = person.online && (here || canEnterZoneHere(person.online.zoneSlug)) ? person.online : null;
  const isMuted = useGameStore((s) => s.mutedPlayerIds.has(person.userId));

  return (
    <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
      <Button variant="ghost" size="sm" icon="arrow-left" onClick={onBack} style={{ alignSelf: 'flex-start' }}>Toutes les personnes</Button>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
        <PersonAvatar userId={person.userId} name={person.name} size={112} highlight={isSelf} />
        <h3 style={{ margin: 0, font: '700 20px/1.25 var(--font-display)' }}>{person.name}{isSelf && ' (toi)'}</h3>
        <span style={{ fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)' }}>
          {person.role ? ROLE_LABELS[person.role] ?? 'Membre' : 'Invité'}
          {person.profileName && ` · ${person.profileName}`}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 'var(--text-sm)' }}>
          <StatusDot status={person.online?.status ?? 'offline'} />
          {person.online ? `${STATUS_LABELS[person.online.status]} · ${person.online.zoneName}` : lastSeenLabel(person.lastSeenAt)}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {canMessage && gated && <ConnectionActions person={person} />}
        {canMessage && !gated && (
          <Button variant="primary" icon="message" onClick={() => { EventBus.emit('ui:open-dm', { userId: person.userId }); }}>
            Envoyer un message
          </Button>
        )}
        {canMessage && !gated && <WhizButton person={person} />}
        {!person.online && canMessage && !gated && (
          <p className="pl-help" style={{ margin: 0, textAlign: 'center' }}>Hors ligne : ton message l&apos;attendra à sa prochaine connexion.</p>
        )}
        {joinable && !isSelf && (
          <Button variant="secondary" icon="map-pin" onClick={() => {
            EventBus.emit('ui:go-to-person', { userId: person.userId, zoneSlug: joinable.zoneSlug, name: person.name });
            onClose();
          }}>
            {here ? 'Rejoindre' : `Rejoindre (${joinable.zoneName})`}
          </Button>
        )}
        {isAdmin && here && !isSelf && (
          <div style={{ display: 'flex', gap: 8 }}>
            {isMuted ? (
              <Button variant="secondary" size="sm" icon="mic" onClick={() => void moderationRequest('unmute', person.userId)} style={{ flex: 1 }}>Rendre la parole</Button>
            ) : (
              <Button variant="secondary" size="sm" icon="mic-off" onClick={() => void moderationRequest('mute', person.userId)} style={{ flex: 1 }}>Couper la parole 5 min</Button>
            )}
            <Button variant="danger" size="sm" icon="log-out" onClick={() => void moderationRequest('kick', person.userId)} style={{ flex: 1 }}>Expulser</Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** En-tête du panneau Personnes : nombre de personnes en ligne et de membres. */
function PeopleSummary() {
  const members = useMembersStore((s) => s.members);
  const online = useCampusOnline();
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--text-label)', color: 'var(--color-text-secondary)' }}>
      <StatusDot status="available" />
      {online.size} en ligne sur le campus · {members.size} membre{members.size > 1 ? 's' : ''}
    </span>
  );
}

export function PeoplePanel({ onClose }: { onClose: () => void }) {
  return (
    <SidePanel
      label="Personnes"
      onClose={onClose}
      header={(
        <header style={panelHeaderStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h2 style={{ margin: 0, font: '700 18px/1.25 var(--font-display)' }}>Personnes</h2>
            <PeopleSummary />
          </div>
          <Button variant="ghost" icon="x" aria-label="Fermer" onClick={onClose} />
        </header>
      )}
    >
      <PeopleBrowser onClose={onClose} />
    </SidePanel>
  );
}

/**
 * Liste des personnes (en ligne d'abord, puis hors ligne) et fiche de chacune.
 * Partagée par le panneau Personnes et l'onglet Personnes du chat.
 */
export function PeopleBrowser({ onClose }: { onClose: () => void }) {
  const members = useMembersStore((s) => s.members);
  const online = useCampusOnline();
  const zonePlayers = useGameStore((s) => s.zonePlayers);
  const displayName = useGameStore((s) => s.displayName);
  const localBubbleId = useLocalBubbleId();
  const playerBubbleIds = usePlayerBubbleIds();
  const proximityUserIds = useProximityUserIds();
  const { userId: localUserId } = sessionInfo();
  const [tab, setTab] = useState<'campus' | 'here' | 'requests'>('campus');
  const connections = useConnectionsStore((s) => s.byUser);
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => { void loadMembers(10_000); void refreshCampusPresence(); void loadConnections(); }, []);

  const people = useMemo(() => {
    const all = new Map<string, Person>();
    for (const m of members.values()) {
      all.set(m.userId, { userId: m.userId, name: m.displayName, online: online.get(m.userId) ?? null, lastSeenAt: m.lastSeenAt, role: m.role, profileName: m.accessProfile?.name ?? null });
    }
    // Invités et membres pas encore dans l'annuaire chargé.
    for (const o of online.values()) {
      if (!all.has(o.userId)) all.set(o.userId, { userId: o.userId, name: o.displayName, online: o, lastSeenAt: null, role: o.userId.startsWith('guest:') ? null : 'MEMBER', profileName: null });
    }
    const self = all.get(localUserId);
    if (self && displayName) self.name = displayName;
    return all;
  }, [members, online, localUserId, displayName]);

  const q = query.trim().toLocaleLowerCase('fr');
  const match = (p: Person) => !q || p.name.toLocaleLowerCase('fr').includes(q);
  const byName = (a: Person, b: Person) => (a.userId === localUserId ? -1 : b.userId === localUserId ? 1 : a.name.localeCompare(b.name, 'fr'));
  const onlineList = [...people.values()].filter((p) => p.online && match(p)).sort(byName);
  const offlineList = [...people.values()].filter((p) => !p.online && match(p))
    .sort((a, b) => (b.lastSeenAt ?? '').localeCompare(a.lastSeenAt ?? '') || a.name.localeCompare(b.name, 'fr'));

  // « Ici » : la bulle si on est dedans, sinon les personnes à proximité, sinon soi.
  const hereIds = localBubbleId
    ? [...zonePlayers.values()].filter((p) => playerBubbleIds.get(p.userId) === localBubbleId).map((p) => p.userId)
    : [localUserId, ...proximityUserIds];
  const hereList = [...new Set(hereIds)].map((id) => people.get(id)).filter((p): p is Person => !!p).sort(byName);

  const requestList = [...connections.values()]
    .filter((c) => c.status === 'pending' && c.direction === 'received')
    .map((c) => people.get(c.userId))
    .filter((p): p is Person => !!p);
  useEffect(() => { if (tab === 'requests' && requestList.length === 0) setTab('campus'); }, [tab, requestList.length]);

  const opened = openId ? people.get(openId) : undefined;

  return (
    <>
      {opened ? (
        <ProfileView person={opened} onBack={() => setOpenId(null)} onClose={onClose} />
      ) : (
        <>
          <div style={{ padding: '12px 16px 8px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Tabs
              aria-label="Qui afficher"
              items={[
                { id: 'campus', label: `Campus · ${online.size}` },
                { id: 'here', label: localBubbleId ? `Bulle · ${hereList.length}` : `À proximité · ${Math.max(0, hereList.length - 1)}` },
                ...(requestList.length > 0 ? [{ id: 'requests' as const, label: `Demandes · ${requestList.length}` }] : []),
              ]}
              value={tab}
              onChange={setTab}
            />
            {tab === 'campus' && (
              <Input aria-label="Chercher une personne" placeholder="Chercher une personne…" value={query} onChange={(e) => setQuery(e.target.value)} />
            )}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 16px' }}>
            {tab === 'requests' ? (
              <>
                <SectionLabel>Veulent se connecter avec toi</SectionLabel>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {requestList.map((p) => <PersonRow key={p.userId} person={p} you={false} onOpen={() => setOpenId(p.userId)} />)}
                </ul>
              </>
            ) : tab === 'campus' ? (
              <>
                <SectionLabel>En ligne · {onlineList.length}</SectionLabel>
                {onlineList.length === 0 ? (
                  <p className="pl-help" style={{ margin: '4px 8px' }}>{q ? 'Personne de ce nom en ligne.' : 'Connexion en cours…'}</p>
                ) : (
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {onlineList.map((p) => <PersonRow key={p.userId} person={p} you={p.userId === localUserId} onOpen={() => setOpenId(p.userId)} />)}
                  </ul>
                )}
                {offlineList.length > 0 && (
                  <>
                    <SectionLabel>Hors ligne · {offlineList.length}</SectionLabel>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {offlineList.map((p) => <PersonRow key={p.userId} person={p} you={false} onOpen={() => setOpenId(p.userId)} />)}
                    </ul>
                  </>
                )}
              </>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {hereList.map((p) => <PersonRow key={p.userId} person={p} you={p.userId === localUserId} onOpen={() => setOpenId(p.userId)} />)}
              </ul>
            )}
          </div>
        </>
      )}
    </>
  );
}
